import { Inject } from '@odinjs/odin';
import { DynamicFieldsAPI, FormAPI, ModalService } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, PseudonymAppointmentsStep_4Aliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { QuizItems } from '../../interfaces/appointment';
import { ObjectComparatorUtils } from '../../utils/object-comparator-utils';
import PseudonymAppointmentsBaseController from './pseudonym-appointments-base-controller';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import { QuizMetadata } from './pseudonym-interfaces';
import PseudonymIntegratedScheduleService from './pseudonym-service/pseudonym-integrated-schedule-service';
import { AppointmentsSteps, NO, YES } from './pseudonym-types';

interface QuizAnswer {
  [key: string]: Value;
  label: string;
  value: string;
}

const DefaultQuizParams = {
  READ_ONLY: NO,
  TRANSFER: NO
};

@Controller({ component: PseudonymAppointmentsStep_4Aliases.LAYOUT_QUIZ, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_STEP_4 })
export default class PseudonymAppointmentsStep4Controller extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsStep_4Aliases.FORM_QUIZ)
    quizFormApi!: FormAPI;

  @Inject(PseudonymAppointmentsStep_4Aliases.DYNAMIC_FIELDS_QUIZ)
    quizDynamicFieldsApi!: DynamicFieldsAPI;

  @Inject('PseudonymIntegratedScheduleService')
    pseudonymIntegratedScheduleService!: PseudonymIntegratedScheduleService;

  @OnMount(PseudonymAppointmentsStep_4Aliases.LAYOUT_QUIZ)
  onMountLayoutQuiz(): void {
    PseudonymAppointmentModel.stepFour.quizFormApi = this.quizFormApi;
    PseudonymAppointmentModel.stepFour.quizDynamicFieldsApi = this.quizDynamicFieldsApi;
    PseudonymAppointmentModel.stepFour.populateQuizTab = () => this.populateQuizTab();
  }

  @OnChange({ target: PseudonymAppointmentsStep_4Aliases.FORM_QUIZ })
  onChangeFormQuiz(): void {
    const answerFormQuiz: Record<string, Value> = this.quizFormApi.getRecord();
    const dynamicMetada = PseudonymAppointmentModel.stepFour.quizDynamicMetada;
    const arrayPromises = [] as Array<object>;
    this.context.set({ alertQuiz: '' });
    Object.entries(answerFormQuiz).forEach(([key, value]) => {
      const answer = this.formatQuizAnswer(value);
      this.updateQuizAnswer(key, answer, dynamicMetada.id, dynamicMetada.children, arrayPromises);
    });
    Promise.all(arrayPromises).then(() => {
      const textAlert = this.context.get('alertQuiz');
      if (textAlert) {
        ModalService.openDefault({
          closeable: true,
          contextData: { dsAlert: textAlert },
          dockButtons: [],
          fragment: FragmentNames.ALERT_QUIZ,
          onClose: ()=> {return;},
          title: 'patient.label.notes',
        });
      }
    })
  }

  private formatQuizAnswer(value: Value): QuizAnswer {
    const answer: QuizAnswer = { label: '', value: '' };

    if (ObjectComparatorUtils.isObject(value)) {
      return value as QuizAnswer;
    }

    answer.label = value as string;

    return answer;
  }

  private updateQuizAnswer(key: string, answer: QuizAnswer, fatherId: string, children: Array<QuizMetadata>, arrayPromises: Array<object>): void {
    if (children == null || children?.length === 0) {
      return;
    }

    children.forEach((value) => {
      if (value.entry === key && value.answer !== answer.label) {
        value.answer = answer.label;
        value.sequenceAnswer = answer.value;
        this.saveQuizItem(fatherId, value, arrayPromises);
      }

      this.updateQuizAnswer(key, answer, value.id, value.children, arrayPromises);
    });
  }

  private saveQuizItem(seqAgeintItem: string, quizItem: QuizMetadata, arrayPromises: Array<object>): void {
    const dynamicMetada = PseudonymAppointmentModel.stepFour.quizDynamicMetada;
    const body = {
      answer: quizItem.answer,
      sequenceAnswer: quizItem.sequenceAnswer !== '' ? Number(quizItem.sequenceAnswer) : null,
      sequenceIntegratedSchedule: Number(dynamicMetada.id),
      sequenceItemIntegratedSchedule: Number(seqAgeintItem),
      sequenceStructureQuestion: Number(quizItem.entry)
    };

    const promise = HttpService.put(APPLICATION_ENDPOINTS.PUT_QUIZ_ITEM, { nrSeqAgeint: Number(dynamicMetada.id) }, { body });
    arrayPromises.push(promise);
    promise.then((response) => {
      const quiz = response.body as Array<{ alert?: string, sequenceItemIntegratedSchedule?: number, sequenceStructure?: number, visible?: boolean }>;
      let textAlert = '';
      quiz.forEach((value) => {
        value.visible = String(value.visible) === YES;
        textAlert += this.getAlert(value, body, quizItem);
      });
      textAlert = textAlert.replaceAll('\n', '<br>');
      const alertQuizValue = this.context.get('alertQuiz') as string;
      if (textAlert) {
        textAlert += '<br>' + alertQuizValue;
        this.context.set({ alertQuiz: textAlert });
      }
      const quizItems = quiz as Array<QuizItems>;
      this.setQuizItemVisibility(quizItems, dynamicMetada.children);
      this.quizDynamicFieldsApi.restoreState(true);
      this.quizDynamicFieldsApi.setMetadata(dynamicMetada);
      this.checkQuizRequiredFields(dynamicMetada.children);
    });
  }

  private getAlert(value: Record<string, Value>, body: Record<string, Value>, quizItem: QuizMetadata): string {
    if (value.sequenceItemIntegratedSchedule == body.sequenceItemIntegratedSchedule
    && value.sequenceStructure == body.sequenceStructureQuestion && value.alert) {
      const alertStr = value.alert as string;
      const allAlerts = alertStr.split('@;');
      let dsAlert = '';
      for (const alertValue of allAlerts) {
        const cdAlert = alertValue.split('@:');
        if (cdAlert.length == 2 && cdAlert[1] && !cdAlert[0] || cdAlert[0] == quizItem.sequenceAnswer) {
          dsAlert += `${cdAlert[1]}\n`;
        } else if (cdAlert.length == 1 && cdAlert[0]) {
          dsAlert += `${cdAlert[0]}\n`;
        }
      }
      return dsAlert;
    }
    return '';
  }

  private setQuizItemVisibility(quizItems: QuizItems[], children: QuizMetadata[]): void {
    if (!quizItems || children == null || children?.length === 0) {
      return;
    }

    children.forEach((value) => {
      quizItems.forEach(item => {
        if (value?.entry && item?.sequenceStructure?.toString() && item?.sequenceStructure?.toString() === value?.entry) {
          value.visibilityCondition = item?.visible;
        }
      });
      this.setQuizItemVisibility(quizItems, value.children);
    });
  }

  private checkQuizRequiredFields(children: Array<QuizMetadata>): void {
    if (children == null || children?.length === 0) {
      return;
    }

    this.setStepQuizDone(true);

    children.forEach(value => {
      if (value.required && !value.answer && value.visibilityCondition) {
        this.setStepQuizDone(false);
      }

      this.checkQuizRequiredFields(value.children);
    });

    this.setWizardStepDone(AppointmentsSteps.STEP_4_QUIZ, this.getStepQuizDone());
  }

  populateQuizTab(): Promise<void> {
    return this.pseudonymIntegratedScheduleService.getIntegratedScheduleSequenceAndSaveInternalProcedures()
      .then(() => this.setQuizMetadata(PseudonymAppointmentModel.integratedScheduleSequence));
  }

  private setQuizMetadata(integratedScheduleSequence: number): Promise<void> {
    if (!integratedScheduleSequence) {
      return Promise.resolve();
    }

    const params = {
      readOnly: DefaultQuizParams.READ_ONLY,
      seqAgeInt: integratedScheduleSequence,
      transfer: DefaultQuizParams.TRANSFER,
    }

    const quizDynamicFields = PseudonymAppointmentModel.stepFour.quizDynamicFieldsApi;
    const quizForm = PseudonymAppointmentModel.stepFour.quizFormApi;
    quizDynamicFields.restoreState(true);
    quizDynamicFields.setMetadata({} as QuizMetadata);
    quizForm.setLoading(true);

    return HttpService.get<QuizMetadata>(APPLICATION_ENDPOINTS.GET_QUIZ_METADATA, params)
      .then(response => {
        const body = response.body;
        const dynamicMetada = body as QuizMetadata;

        if (!dynamicMetada || !dynamicMetada.children) {
          PseudonymAppointmentModel.hasMetadataQuiz = false;
          PseudonymAppointmentModel.stepFour.quizDynamicMetada = {} as QuizMetadata;
          return;
        } if (PseudonymAppointmentModel.stepFour.quizDynamicMetada.id === dynamicMetada.id) {
          quizDynamicFields.setMetadata(PseudonymAppointmentModel.stepFour.quizDynamicMetada);
          this.checkQuizRequiredFields(PseudonymAppointmentModel.stepFour.quizDynamicMetada.children);
          quizForm.setLoading(false);
          return;
        }
        quizDynamicFields.restoreState(true);
        quizDynamicFields.setMetadata(dynamicMetada);
        this.checkQuizRequiredFields(dynamicMetada.children);
        quizForm.setRecord({});
        quizForm.setLoading(false);

        PseudonymAppointmentModel.hasMetadataQuiz = true;
        PseudonymAppointmentModel.stepFour.quizDynamicMetada = dynamicMetada;
      });
  }

  private setStepQuizDone(isDone: boolean): void {
    this.context.set({ stepQuizDone: isDone });
  }

  private getStepQuizDone(): boolean {
    return this.context.get('stepQuizDone') as boolean;
  }

}
